import 'leaflet/dist/leaflet.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.Default.css';
import '../../../utils/leafletGlobal';
import 'leaflet-rotatedmarker';

import L, { LatLngTuple } from 'leaflet';
import { memo, useContext, useMemo, useState } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useParams } from 'react-router-dom';

import { PLACE_STATUS_VISITED } from '../../../constants.ts';
import { SelectedNodeContext } from '../../../contexts/node/SelectedNodeContext.ts';
import { SelectedVehicleContext } from '../../../contexts/vehicle/SelectedVehicleContext.ts';
import { NodeType } from '../../../schema/Node.ts';
import { Place } from '../../../schema/Place.ts';
import { RoadmapType } from '../../../schema/Roadmap.ts';
import { Territory } from '../../../schema/Territory.ts';
import { IVehicle } from '../../../schema/Vehicle.ts';
import { useGroupedVehiclesByTerritory } from '../../../services/Vehicle.ts';
import { mapAttribution, mapUrl } from '../../../utils/mapUtils.ts';
import { getNextPlace } from '../../../utils/roadmapUtils.ts';
import { GetMapCenterCoordinated } from '../../../utils/utils.ts';
import RecenterMap from '../../Dashboard/Map/RecenterMap.tsx';
import { ResetDefaultButton } from '../../Dashboard/Map/ResetDefaultButton.tsx';
import { ShowOnlyRoadmapNodesButton } from '../../Dashboard/Map/ShowOnlyRoadmapNodesButton.tsx';
import { NodeMarker } from '../ui/NodeMarker.tsx';
import { VehicleMarker } from '../ui/VehicleMarker.tsx';

function createClusterIcon(cluster: { getChildCount: () => number }) {
  const count = cluster.getChildCount();
  return L.divIcon({
    className: '',
    html: `<div class="node-cluster-icon">${count}</div>`,
    iconSize: L.point(32, 32, true),
  });
}

interface DashboardMapProps {
  territory: Territory;
  dispatchGeography: NodeType[];
  roadmapData: RoadmapType | null | undefined;
  clusterNodes?: boolean;
}

export const DashboardMap = memo(function DashboardMap({
  territory,
  dispatchGeography,
  roadmapData,
  clusterNodes = false,
}: DashboardMapProps) {
  const params = useParams() as { territorySlug: string };
  const [showOnlyRoadmapNodes, setShowOnlyRoadmapNodes] = useState(true);
  const { selectedNode } = useContext(SelectedNodeContext);
  const { selectedVehicle } = useContext(SelectedVehicleContext);
  const { grouped } = useGroupedVehiclesByTerritory(params.territorySlug);

  const roadmapNodeNames = useMemo(
    () =>
      roadmapData?.places
        ?.filter((place: Place) => place.status !== PLACE_STATUS_VISITED)
        .map((place: Place) => place?.name)
        .filter(Boolean) ?? [],
    [roadmapData],
  );
  const nextNodeName = roadmapData?.places?.length ? getNextPlace(roadmapData.places).name : undefined;

  // Recompute only on selection/data change, not on the selected vehicle's live position
  // (polled every 15s) — otherwise the map would yank back to center on every poll tick.
  const [center, zoom]: [LatLngTuple, number] = useMemo(() => {
    const selectedVehicleData = [
      ...grouped.InOperation,
      ...grouped.PlannedOperation,
      ...grouped.Inactive,
    ].find(v => v.id === selectedVehicle?.id);

    if (selectedVehicleData?.latitude != null && selectedVehicleData?.longitude != null) {
      return [
        [selectedVehicleData.latitude, selectedVehicleData.longitude],
        territory.config.default_map_zoom + 1,
      ];
    }
    return [GetMapCenterCoordinated(dispatchGeography), territory.config.default_map_zoom];
  }, [selectedVehicle?.id, dispatchGeography, territory]);

  const visibleNodes =
    roadmapNodeNames.length > 0 && showOnlyRoadmapNodes
      ? dispatchGeography.filter(
          node =>
            roadmapNodeNames.includes(node.name) ||
            node.name === selectedNode?.name ||
            node.name === nextNodeName,
        )
      : dispatchGeography;

  const getNodeVariant = (node: NodeType) => {
    if (selectedNode?.name === node.name) return { variant: 'selected-node' as const, size: 24 };
    if (node.name === nextNodeName) return { variant: 'next-node' as const, size: 24 };
    if (roadmapNodeNames.includes(node.name)) return { variant: 'in-roadmap' as const, size: 15 };
    return { variant: 'not-in-roadmap' as const, size: 15 };
  };

  const withPosition = <T extends IVehicle>(vehicles: T[]) =>
    vehicles.filter(
      (v): v is T & { latitude: number; longitude: number } =>
        v.latitude !== null && v.longitude !== null,
    );

  const getVehicleVariant = (vehicle: IVehicle) =>
    vehicle.id === selectedVehicle?.id ? 'selected' : undefined;

  const nodeMarkers = visibleNodes.map(node => (
    <NodeMarker key={node.id} node={node} popup="simple" {...getNodeVariant(node)} />
  ));

  return (
    <MapContainer center={center} scrollWheelZoom={true}>
      <RecenterMap lat={center[0]} lng={center[1]} zoom={zoom} />
      <TileLayer attribution={mapAttribution} url={mapUrl} />
      <ResetDefaultButton center={center} zoom={zoom} />
      {roadmapData && (
        <ShowOnlyRoadmapNodesButton
          showOnlyRoadmapNodes={showOnlyRoadmapNodes}
          setShowOnlyRoadmapNodes={setShowOnlyRoadmapNodes}
        />
      )}
      {clusterNodes ? (
        <MarkerClusterGroup chunkedLoading maxClusterRadius={30} iconCreateFunction={createClusterIcon}>
          {nodeMarkers}
        </MarkerClusterGroup>
      ) : (
        nodeMarkers
      )}
      {withPosition(grouped.InOperation).map(vehicle => (
        <VehicleMarker
          key={vehicle.id}
          vehicle={vehicle}
          variant={getVehicleVariant(vehicle) ?? 'in-operation'}
          rotationAngle={vehicle.heading}
        />
      ))}
      {withPosition(grouped.PlannedOperation).map(vehicle => (
        <VehicleMarker
          key={vehicle.id}
          vehicle={vehicle}
          variant={getVehicleVariant(vehicle) ?? 'inactive'}
          rotationAngle={vehicle.heading}
        />
      ))}
      {withPosition(grouped.Inactive).map(vehicle => (
        <VehicleMarker
          key={vehicle.id}
          vehicle={vehicle}
          variant={getVehicleVariant(vehicle) ?? 'inactive'}
          rotationAngle={vehicle.heading}
        />
      ))}
    </MapContainer>
  );
});
